import { motion } from 'framer-motion';
import { ExternalLink, ShoppingBag, Heart, Star, Gift } from 'lucide-react';

const SHOPIFY_LINK = 'https://cuavena.myshopify.com';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function ShopifyStorePage() {
  return (
    <div data-testid="shopify-page">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-4">
              Our Collection
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2A2421] mb-6 leading-tight">
              Explore Our<br />Faith-Rooted Shop
            </h1>
            <p className="font-sans text-base md:text-lg text-[#7A736E] max-w-2xl mx-auto leading-relaxed mb-6">
              Discover faith-inspired products, journals, and meaningful items designed to uplift, encourage, and strengthen your walk with God. Every purchase supports a mission rooted in faith and purpose.
            </p>
            <div className="inline-block bg-[#F5F2EB] border border-[#E8E1D9] rounded-xl px-6 py-3 mb-10" data-testid="launch-offer-banner">
              <p className="font-sans text-sm text-[#2A2421]">
                <span className="font-semibold text-[#A67A5B]">Journal Launch Offer:</span> Use code <span className="font-semibold tracking-wide">FAITH18</span> for 18% off the Divine Intervention Journal
              </p>
            </div>
            <div>
              <a
                href={SHOPIFY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shopify-main-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#A67A5B] text-white px-10 py-5 rounded-full font-medium tracking-wide text-lg shadow-lg hover:bg-[#8C654A] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
              >
                <ShoppingBag size={22} />
                Shop on Shopify
                <ExternalLink size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Store Categories */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421] mb-4">
              What You'll Find
            </h2>
            <p className="font-sans text-base text-[#7A736E] max-w-xl mx-auto">
              Our growing collection of faith-centered products
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Faith Journals',
                desc: 'Guided journals and devotionals designed to deepen your daily walk with God.',
              },
              {
                icon: Star,
                title: 'Inspirational Products',
                desc: 'Meaningful items that carry messages of faith, hope, and encouragement.',
              },
              {
                icon: Gift,
                title: 'Gift Ideas',
                desc: 'Thoughtful faith-based gifts perfect for loved ones who need encouragement.',
              },
            ].map((cat, i) => (
              <motion.a
                key={cat.title}
                href={SHOPIFY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.6 }}
                className="group p-10 rounded-2xl bg-[#FDFBF7] border border-[#E8E1D9] hover:shadow-xl hover:-translate-y-1 transition-all duration-500 text-center cursor-pointer"
                data-testid={`shop-category-${i}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#A67A5B]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#A67A5B]/20 transition-colors duration-300">
                  <cat.icon className="text-[#A67A5B]" size={28} />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#2A2421] mb-3">
                  {cat.title}
                </h3>
                <p className="font-sans text-sm text-[#7A736E] leading-relaxed mb-4">
                  {cat.desc}
                </p>
                <span className="inline-flex items-center gap-1 text-[#A67A5B] text-sm font-medium group-hover:gap-2 transition-all duration-300">
                  Browse <ArrowIcon />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 md:py-28 bg-[#2A2421]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <div className="w-12 h-[1px] bg-[#C59B76] mx-auto mb-8" />
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white mb-6">
              Every Purchase Supports a Purpose
            </h2>
            <p className="font-sans text-base text-white/55 leading-relaxed mb-10 max-w-2xl mx-auto">
              When you shop with Led By Faith Studios, you're supporting a mission of faith, healing, and encouragement. Thank you for being part of this journey.
            </p>
            <a
              href={SHOPIFY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shopify-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#C59B76] text-white px-10 py-4 rounded-full font-medium tracking-wide text-lg shadow-lg hover:bg-[#A67A5B] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              <ShoppingBag size={20} />
              Visit Our Shopify Store
            </a>
            <div className="w-12 h-[1px] bg-[#C59B76] mx-auto mt-10" />
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}
